import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import EmptyAccessState from '@/app/components/EmptyAccessState';
import { useSession } from '@/app/providers/session-provider';
import { getShellComponent } from '@/app/shells/shell-registry';
import LoginPage from '@/modules/auth/pages/LoginPage';
import ChangePasswordPage from '@/modules/auth/pages/ChangePasswordPage';
import AcceptInvitePage from '../../modules/auth/pages/AcceptInvitePage';
import {
  getAdminRoutes,
  getAppRoutes,
  getAuxiliaryRoutes,
  getRoutePath,
  getRoutesByShell,
  resolveTenantDefaultRoute,
  resolveTenantRoutes,
  ROUTE_IDS,
  SHELL_IDS,
} from '@/app/router/route-registry';
import { useTenant } from '@/tenant/tenant-provider';
import AdminLoginPage from '@/modules/auth/pages/AdminLoginPage';

function getRequestedPathname() {
  if (typeof window === 'undefined' || !window.location?.pathname) {
    return '/';
  }

  return window.location.pathname;
}

export default function AppRouter() {
  const { access, hasAvailableRoutes, tenant } = useTenant();
  const {
    canAccessRoute,
    canAccessRouteId,
    isAuthenticated,
    isLoadingSession,
    canAccessPlatformAdmin,
    isWorkspaceAccess,
    mustChangePassword,
  } = useSession();
  const adminRoutes = getAdminRoutes().filter(canAccessRoute);
  const tenantAppRoutes = resolveTenantRoutes(getAppRoutes(), access);
  const tenantLevelAuxiliaryRoutes = resolveTenantRoutes(getAuxiliaryRoutes(), access);
  const availableAppRoutes = tenantAppRoutes.filter(canAccessRoute);
  const availableAuxiliaryRoutes = tenantLevelAuxiliaryRoutes.filter(canAccessRoute);
  const routesByShell = getRoutesByShell(availableAppRoutes);
  const auxiliaryRoutesByShell = getRoutesByShell(availableAuxiliaryRoutes);
  const tenantRoutes = routesByShell[SHELL_IDS.TENANT] || [];
  const tenantAccessibleAuxiliaryRoutes = auxiliaryRoutesByShell[SHELL_IDS.TENANT] || [];
  const TenantShell = getShellComponent(SHELL_IDS.TENANT);
  const AdminShell = getShellComponent(SHELL_IDS.ADMIN);
  const adminDefaultRoute = adminRoutes[0] || null;
  const routeAccessDescription = isAuthenticated
    ? 'La sessione corrente non ha accesso a questa sezione o non espone route consentite.'
    : "La sessione corrente non ha route accessibili. Effettua nuovamente l'accesso per continuare.";
  const hasAccessibleRoutes = tenantRoutes.length > 0 || tenantAccessibleAuxiliaryRoutes.length > 0;
  const fallbackRoute = resolveTenantDefaultRoute(tenant, availableAppRoutes);
  const settingsPath = getRoutePath(ROUTE_IDS.SETTINGS);
  const canAccessSettings = canAccessRouteId(ROUTE_IDS.SETTINGS);
  const wildcardDescription =
    fallbackRoute?.path && isAuthenticated
      ? `${routeAccessDescription} Usa la navigazione disponibile per continuare.`
      : routeAccessDescription;
  const wildcardActionLabel = fallbackRoute?.path ? 'Vai alla sezione disponibile' : '';
  const wildcardActionTo = fallbackRoute?.path || '';
  const requestedPathname = getRequestedPathname();
  const isAdminEntryRequest = requestedPathname === '/admin/login' || requestedPathname.startsWith('/admin/');
  const loginPath = isAdminEntryRequest ? '/admin/login' : '/login';
  const adminDefaultPath = adminDefaultRoute?.path || '/admin';

  if (isLoadingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--surface-raised)] px-6">
        <div className="w-full max-w-md rounded-3xl border border-[var(--border)] bg-[var(--surface-raised)] p-8 text-center shadow-sm">
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[var(--success-text)]">
            Webtika Pre-Alpha
          </p>
          <h1 className="mt-3 text-2xl font-bold text-[var(--text-primary)]">Verifica sessione</h1>
          <p className="mt-2 text-sm text-[var(--text-secondary)]">
            Controllo token e profilo utente dal backend auth.
          </p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      {!isAuthenticated ? (
        <Routes>
          <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="*" element={<Navigate to={loginPath} replace />} />
        </Routes>
      ) : mustChangePassword ? (
        <Routes>
          <Route path="/change-password" element={<ChangePasswordPage />} />
          <Route path="*" element={<Navigate to="/change-password" replace />} />
        </Routes>
      ) : canAccessPlatformAdmin && !isWorkspaceAccess ? (
        <Routes>
          <Route path="/login" element={<Navigate to={adminDefaultPath} replace />} />
          <Route path="/admin/login" element={<Navigate to={adminDefaultPath} replace />} />
          <Route path="/" element={<Navigate to={adminDefaultPath} replace />} />
          <Route path="/admin" element={<AdminShell />}>
            <Route index element={<Navigate to={adminDefaultPath} replace />} />
            {adminRoutes.map((route) => (
              <Route
                key={route.id}
                path={route.path.replace(/^\/admin\/?/, '')}
                element={route.element}
              />
            ))}
            <Route path="*" element={<Navigate to={adminDefaultPath} replace />} />
          </Route>
          <Route path="*" element={<Navigate to={adminDefaultPath} replace />} />
        </Routes>
      ) : !hasAvailableRoutes || !hasAccessibleRoutes ? (
        <Routes>
          <Route
            path="*"
            element={
              <EmptyAccessState
                description={routeAccessDescription}
                actionLabel={canAccessSettings ? 'Apri impostazioni' : ''}
                actionTo={canAccessSettings ? settingsPath : ''}
              />
            }
          />
        </Routes>
      ) : (
        <Routes>
          <Route path="/login" element={<Navigate to={fallbackRoute?.path || '/'} replace />} />
          <Route path="/admin/login" element={<Navigate to={fallbackRoute?.path || '/'} replace />} />
          <Route
            path="/admin/*"
            element={<Navigate to={isWorkspaceAccess ? '/' : fallbackRoute?.path || '/'} replace />}
          />
          <Route path="/" element={<TenantShell />}>
            {tenantRoutes.map((route) =>
              route.path === '/' ? (
                <Route key={route.id} index element={route.element} />
              ) : (
                <Route key={route.id} path={route.path.slice(1)} element={route.element} />
              )
            )}

            {tenantAccessibleAuxiliaryRoutes.map((route) => (
              <Route
                key={route.id}
                path={route.path.slice(1)}
                element={<Navigate to={route.redirectTo} replace />}
              />
            ))}

            <Route
              path="*"
              element={
                <EmptyAccessState
                  title="Route non disponibile"
                  description={wildcardDescription}
                  actionLabel={wildcardActionLabel}
                  actionTo={wildcardActionTo}
                />
              }
            />
          </Route>
        </Routes>
      )}
    </BrowserRouter>
  );
}
